import type {
  MindstringPrototype,
  MindstringPrototypeMetadataResponse,
  MindstringPrototypeUiMetadata,
} from "@/generated/fastapi";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";

export const MINDSTRING_PROTOTYPE_JOB_ACTIVITY = "job_activity" as const;
export const MINDSTRING_PROTOTYPE_LEARNING_OUTCOME =
  "learning_outcome" as const;
export const MINDSTRING_PROTOTYPE_WORKPLACE_CHARACTERISTIC =
  "workplace_characteristic" as const;
export const MINDSTRING_PROTOTYPE_INDIVIDUAL_DESCRIPTION =
  "individual_description" as const;

export const mindstringPrototypeCodes = [
  MINDSTRING_PROTOTYPE_JOB_ACTIVITY,
  MINDSTRING_PROTOTYPE_LEARNING_OUTCOME,
  MINDSTRING_PROTOTYPE_WORKPLACE_CHARACTERISTIC,
  MINDSTRING_PROTOTYPE_INDIVIDUAL_DESCRIPTION,
] as const;

export const publicMindstringPrototypeCodes = [
  MINDSTRING_PROTOTYPE_JOB_ACTIVITY,
  MINDSTRING_PROTOTYPE_LEARNING_OUTCOME,
] as const satisfies readonly MindstringPrototype[];

export const adminExperimentalMindstringPrototypeCodes = [
  MINDSTRING_PROTOTYPE_INDIVIDUAL_DESCRIPTION,
  MINDSTRING_PROTOTYPE_WORKPLACE_CHARACTERISTIC,
] as const satisfies readonly MindstringPrototype[];

export function getVisibleMindstringPrototypeCodes(
  isUserAdmin: boolean,
): readonly MindstringPrototype[] {
  return isUserAdmin
    ? [...publicMindstringPrototypeCodes, ...adminExperimentalMindstringPrototypeCodes]
    : publicMindstringPrototypeCodes;
}

export function isVisibleMindstringPrototype(
  value: unknown,
  isUserAdmin: boolean,
): value is MindstringPrototype {
  return (
    isMindstringPrototype(value) &&
    (getVisibleMindstringPrototypeCodes(isUserAdmin) as readonly string[]).includes(
      value,
    )
  );
}

export function normalizeVisibleMindstringPrototype(
  value: unknown,
  isUserAdmin: boolean,
  fallback: MindstringPrototype = MINDSTRING_PROTOTYPE_JOB_ACTIVITY,
): MindstringPrototype {
  return isVisibleMindstringPrototype(value, isUserAdmin) ? value : fallback;
}

export function isMindstringPrototype(
  value: unknown,
): value is MindstringPrototype {
  return (
    typeof value === "string" &&
    (mindstringPrototypeCodes as readonly string[]).includes(value)
  );
}

export function normalizeMindstringPrototype(
  value: unknown,
  fallback: MindstringPrototype = MINDSTRING_PROTOTYPE_JOB_ACTIVITY,
): MindstringPrototype {
  return isMindstringPrototype(value) ? value : fallback;
}

export const mindstringPrototypes: Record<MindstringPrototype, string> =
  Object.freeze({
    [MINDSTRING_PROTOTYPE_JOB_ACTIVITY]: "job activity",
    [MINDSTRING_PROTOTYPE_LEARNING_OUTCOME]: "learning outcome",
    [MINDSTRING_PROTOTYPE_WORKPLACE_CHARACTERISTIC]: "workplace characteristic",
    [MINDSTRING_PROTOTYPE_INDIVIDUAL_DESCRIPTION]: "individual description",
  });

export type MindstringPrototypeUiMetadataForFrontend = Omit<
  MindstringPrototypeUiMetadata,
  "icon"
> & {
  icon: GenericIconType;
};

export type MindstringPrototypeUiMetadataMap = Record<
  MindstringPrototype,
  MindstringPrototypeUiMetadataForFrontend
>;

// Backend-owned source: GET /ssot/mindstring_prototypes.
// This synchronous mirror keeps existing UI surfaces stable while remaining
// typed against the generated backend contract.
export const mindstringPrototypesSlang = Object.freeze({
  [MINDSTRING_PROTOTYPE_JOB_ACTIVITY]: {
    label: "skills",
    description:
      "Skills and competencies directly related to tasks and responsibilities.",
    icon: "job_activity",
    aliases: ["skill", "skills"],
  },
  [MINDSTRING_PROTOTYPE_LEARNING_OUTCOME]: {
    label: "knowledge",
    description:
      "Knowledge and understanding acquired through education or practice.",
    icon: "learning_outcome",
    aliases: ["knowledge"],
  },
  [MINDSTRING_PROTOTYPE_WORKPLACE_CHARACTERISTIC]: {
    label: "preferences",
    description:
      "Preferred work environments, conditions, and organizational cultures.",
    icon: "workplace_characteristic",
    aliases: ["preference", "preferences"],
  },
  [MINDSTRING_PROTOTYPE_INDIVIDUAL_DESCRIPTION]: {
    label: "awareness",
    description:
      "Insights into personal traits, strengths, and areas for development.",
    icon: "individual_description",
    aliases: ["awareness"],
  },
} satisfies MindstringPrototypeUiMetadataMap);

export const mindstringPrototypeAliases = Object.freeze(
  Object.fromEntries(
    Object.entries(mindstringPrototypesSlang).flatMap(([prototype, metadata]) =>
      metadata.aliases.map((alias) => [alias, prototype]),
    ),
  ) as MindstringPrototypeMetadataResponse["aliases"],
);

export function normalizeMindstringPrototypeMetadataResponse(
  response: MindstringPrototypeMetadataResponse,
): MindstringPrototypeUiMetadataMap {
  return Object.fromEntries(
    Object.entries(response.prototypes)
      .filter(([prototype]) => isMindstringPrototype(prototype))
      .map(([prototype, metadata]) => [
        prototype,
        {
          ...metadata,
          icon: metadata.icon as GenericIconType,
          aliases: metadata.aliases ?? [],
        },
      ]),
  ) as MindstringPrototypeUiMetadataMap;
}
